import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import useDebounce from '@/components/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  type OnFarmImplantLookupOption,
  type OnFarmImplantSession,
  onFarmImplantSessionFilterOptions,
  onFarmImplantSessionsOptions,
} from '@/services/on-farm-implant';

import { CreateSessionDialog } from './components/CreateSessionDialog';
import { SessionsGrid } from './components/SessionsGrid';

const PAGE_SIZE = 25;

const SessionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const accountId = searchParams.get('aId') ?? undefined;
  const tripId = searchParams.get('tripId') ?? undefined;
  const createdById = searchParams.get('createdById') ?? undefined;
  const sessionDate = searchParams.get('date') ?? undefined;
  const skip = Number(searchParams.get('skip') ?? 0);

  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');
  const debouncedSearch = useDebounce(searchText);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const updateParams = useCallback(
    (updates: Record<string, string | null>, { resetPage = true } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          if (resetPage) {
            next.delete('skip');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const current = searchParams.get('q') ?? '';
    if (debouncedSearch !== current) {
      updateParams({ q: debouncedSearch || null });
    }
  }, [debouncedSearch, searchParams, updateParams]);

  const { data, isFetching } = useQuery(
    onFarmImplantSessionsOptions({
      accountId,
      tripId,
      createdById,
      sessionDate,
      search: searchParams.get('q') ?? undefined,
      skip,
      take: PAGE_SIZE,
    }),
  );

  const { data: filterOptions } = useQuery(onFarmImplantSessionFilterOptions());

  const sessions = data?.items ?? [];
  const hasMore = data?.hasMore ?? false;

  const openSession = useCallback(
    (session: OnFarmImplantSession) => {
      const params = new URLSearchParams({
        sessionId: session.sessionId,
        sId: session.siteId,
      });
      if (session.accountId) {
        params.set('aId', session.accountId);
      }
      navigate(`/on-farm-implant/entry?${params.toString()}`);
    },
    [navigate],
  );

  const renderClearButton = (label: string, onClear: () => void) => (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={`${t('common.clear')} ${label}`}
      onClick={onClear}
    >
      <i className="pi pi-times text-xs" />
    </Button>
  );

  const renderLookupSelect = (
    key: 'tripId' | 'aId' | 'createdById',
    value: string | undefined,
    placeholder: string,
    options: OnFarmImplantLookupOption[] | undefined,
  ) => (
    <div className="flex items-center gap-1">
      <Select
        items={(options ?? []).map((option) => ({
          label: option.name,
          value: option.id,
        }))}
        value={value ?? null}
        onChange={(next) => updateParams({ [key]: next ?? null })}
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={null}>{placeholder}</SelectItem>
          {options?.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value &&
        renderClearButton(placeholder, () => updateParams({ [key]: null }))}
    </div>
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t('onFarmImplant.sessions.title')}
          </h1>
          <p className="text-sm text-gray-500">
            {t('onFarmImplant.sessions.subtitle')}
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <i className="pi pi-plus" data-icon="inline-start" />
          {t('onFarmImplant.sessions.createNewSession')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
        <div className="relative flex-1">
          <i className="pi pi-search absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-400" />
          <Input
            placeholder={t('onFarmImplant.sessions.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pr-9 pl-9"
          />
          {searchText && (
            <div className="absolute top-1/2 right-1 -translate-y-1/2">
              {renderClearButton(t('common.search'), () => setSearchText(''))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="date"
            className="w-40"
            value={sessionDate ?? ''}
            onChange={(e) => updateParams({ date: e.target.value || null })}
          />
          {sessionDate &&
            renderClearButton(t('onFarmImplant.sessions.sessionDate'), () =>
              updateParams({ date: null }),
            )}
        </div>
        {renderLookupSelect(
          'tripId',
          tripId,
          t('onFarmImplant.sessions.tripNumber'),
          filterOptions?.trips,
        )}
        {renderLookupSelect(
          'aId',
          accountId,
          t('onFarmImplant.sessions.account'),
          filterOptions?.accounts,
        )}
        {renderLookupSelect(
          'createdById',
          createdById,
          t('onFarmImplant.sessions.createdBy'),
          filterOptions?.createdBy,
        )}
      </div>

      {isFetching && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <i className="pi pi-spin pi-spinner text-sm" />
          {t('common.loading')}
        </div>
      )}

      <SessionsGrid sessions={sessions} onOpenSession={openSession} />

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {sessions.length === 0
            ? t('onFarmImplant.sessions.noSessions')
            : t('onFarmImplant.sessions.showingRange', {
                from: skip + 1,
                to: skip + sessions.length,
              })}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={skip === 0}
            onClick={() =>
              updateParams(
                { skip: String(Math.max(skip - PAGE_SIZE, 0)) },
                { resetPage: false },
              )
            }
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasMore}
            onClick={() =>
              updateParams(
                { skip: String(skip + PAGE_SIZE) },
                { resetPage: false },
              )
            }
          >
            {t('common.next')}
          </Button>
        </div>
      </div>

      <CreateSessionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={openSession}
      />
    </div>
  );
};

export const OnFarmImplantSessionsPage = () => {
  const sessionsEnabled = useFeatureFlag('On_Farm_Implant_Sessions');

  return sessionsEnabled ? (
    <SessionsPage />
  ) : (
    <Navigate to="/on-farm-implant/entry" replace />
  );
};
